import React, { useEffect, useMemo, useState } from 'react'
import classnames from 'classnames'

import {
  Button,
  BUTTON_THEME,
  IconButton,
  Input,
  INPUT_THEME,
  Select,
} from '@st-operations/uikit/src/v2/index.js'

import { ConfirmPrompt } from '_components/confirm-prompt'
import { logErrorAsync } from '_lib/utility'
import { getPdpOrganizations } from '_services/pdp-audience-notification-rules'

import AudienceLink from '../../bulk-edit-audiences/components/audience-link'
import { ENV_OPTIONS } from '../../predactiv-admin/helpers'
import { COLLECTION } from '../collection'
import { formatSkipReason } from '../lib/move'

const PAGE_SIZE = 50

const AudienceRow = ({ audience, skipReason, excluded, onToggleExclude }) => (
  <div
    className={classnames('move-to-pdp-audience', {
      'move-to-pdp-audience--excluded': excluded || skipReason,
    })}
  >
    <AudienceLink
      className="move-to-pdp-audience__name"
      href={COLLECTION.audienceUrl(audience._id)}
      name={audience.name}
      id={audience._id}
    />
    <span className="move-to-pdp-audience__meta">
      {audience._id} · {audience.status} · {(audience.destinations || []).length} destinations
    </span>
    <Choose>
      <When condition={skipReason}>
        <span className="move-to-pdp__badge move-to-pdp__badge--muted">
          {formatSkipReason(skipReason)}
        </span>
      </When>
      <Otherwise>
        <IconButton
          onClick={() => onToggleExclude(audience._id)}
          aria-label={excluded ? 'Add audience back to the move' : 'Remove audience from the move'}
          title={excluded ? 'Add back to the move' : 'Remove from the move'}
          iconProps={{
            icon: excluded ? 'add' : 'delete',
            type: 'fill',
            width: 16,
            height: 16,
            color: excluded ? '#13682B' : '#C0392B',
          }}
        />
      </Otherwise>
    </Choose>
  </div>
)

const ReviewStage = ({
  audiences,
  skipReasons,
  excludedIds,
  onToggleExclude,
  onClearExcluded,
  env,
  onEnvChange,
  organization,
  onOrganizationChange,
  onBack,
  onStart,
}) => {
  const [search, setSearch] = useState('')
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const [organizations, setOrganizations] = useState([])
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)

  useEffect(() => {
    getPdpOrganizations(env.value)
      .then((data) =>
        setOrganizations(
          (data?.items || [])
            .map(({ _id, name }) => ({ _id: `${_id}`, name }))
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        )
      )
      .catch((error) => {
        logErrorAsync(error)
        toast.error(`Could not load organizations: ${error?.message}`)
      })
  }, [env])

  const blockedCount = Object.keys(skipReasons).length
  const movable = audiences.filter(({ _id }) => !skipReasons[_id])
  const excludedCount = movable.filter(({ _id }) => excludedIds.has(_id)).length
  const includedCount = movable.length - excludedCount

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return audiences
    return audiences.filter(
      ({ _id, name }) => (name || '').toLowerCase().includes(term) || _id.includes(term)
    )
  }, [audiences, search])

  return (
    <div className="bulk-edit-review">
      <section className="bulk-edit__card">
        <header className="bulk-edit__card-header">
          <h3>2. Choose the PDP organization</h3>
        </header>
        <div className="move-to-pdp__destination">
          <Select
            label="PDP environment"
            useReactSelect
            options={ENV_OPTIONS}
            value={env}
            onChange={(value) => {
              onEnvChange(value)
              // organizations differ per environment
              onOrganizationChange(null)
            }}
          />
          <Select
            label="Organization"
            useReactSelect
            isSearchable
            isClearable
            options={organizations}
            value={organization}
            onChange={(value) => onOrganizationChange(value)}
          />
        </div>
      </section>

      <section className="bulk-edit__card">
        <header className="bulk-edit__card-header">
          <h3>3. Review the audiences</h3>
        </header>

        <p className="bulk-edit__summary">
          <strong>{includedCount}</strong> audiences will be moved.
          <If condition={excludedCount > 0}>
            {' '}
            <strong>{excludedCount}</strong> removed from this move.{' '}
            <button type="button" className="bulk-edit-review__restore" onClick={onClearExcluded}>
              Add all back
            </button>
          </If>
          <If condition={blockedCount > 0}>
            {' '}
            <strong>{blockedCount}</strong> can&apos;t be moved (already in PDP, deleted or a PDP
            taxonomy audience) and will be skipped.
          </If>
        </p>

        <Input
          className="bulk-edit-review__search"
          placeholder="Filter by name or ID"
          theme={INPUT_THEME.SEARCH}
          value={search}
          onChange={setSearch}
        />

        <div className="bulk-edit-review__list">
          <For each="audience" of={filtered.slice(0, visibleCount)}>
            <AudienceRow
              key={audience._id}
              audience={audience}
              skipReason={skipReasons[audience._id]}
              excluded={excludedIds.has(audience._id)}
              onToggleExclude={onToggleExclude}
            />
          </For>
          <If condition={filtered.length > visibleCount}>
            <Button
              theme={BUTTON_THEME.SECONDARY}
              onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
            >
              Show more ({filtered.length - visibleCount} remaining)
            </Button>
          </If>
        </div>
      </section>

      <div className="bulk-edit__stage-actions">
        <Button theme={BUTTON_THEME.SECONDARY} onClick={onBack}>
          Back to Select
        </Button>
        <Button
          onClick={() => setIsConfirmOpen(true)}
          disabled={!organization || includedCount === 0}
        >
          Move {includedCount} audiences
        </Button>
      </div>

      <ConfirmPrompt
        title="Move audiences to PDP"
        description={`Move ${includedCount} audiences to ${organization?.name} (${env.label})? This links them to PDP conversations; it can't be undone from here.`}
        okText="Move"
        cancelText="Cancel"
        isOpen={isConfirmOpen}
        onIsOpenChange={setIsConfirmOpen}
        onOK={onStart}
      />
    </div>
  )
}

export default ReviewStage
